import type {Message, MessageRunState} from '@interfaces/alpinist/message'
import type {RunWaitingFor, StopMode, WSEvent} from '@interfaces/alpinist/ws'

/**
 * Stop & steer (AB#6760): what the chat knows about the run on a conversation
 * and about the messages sent into it.
 *
 * Two sources feed it, and they race. The socket says what changed as it
 * happens. The API says what holds now (`run_active`, `stopping`, each
 * message's `run_state`), and it is the only source after a load, a reload or a
 * reconnect, when the events were missed. But a response is a read taken
 * somewhere between its request and its arrival, so an event that came in
 * after the request went out is newer than it. Every part of the state keeps
 * the client time it last changed, and a read only applies over parts that
 * have not changed since its request started.
 *
 * Pure, so these rules are testable without a socket or a store.
 */

export interface RunStatus {
  /** A run holds the conversation. */
  readonly active: boolean
  /** Stop was asked for and that run has not ended. */
  readonly stopping: boolean
  /**
   * How it stops: `soft` lets Alpinist wrap up with an interim answer, `hard`
   * cancels at once (after a write in flight). Null when not stopping.
   */
  readonly stopMode: StopMode | null
  /** ...and it is letting a write finish first. */
  readonly finishingWrite: boolean
}

export interface MessageRunView {
  readonly runState: MessageRunState
  readonly steered: boolean
  readonly hasAttachments: boolean
  /**
   * It waited behind a run that ended without taking it in, so it opens the
   * next turn. The server has no field for this; the chat saw it happen.
   */
  readonly nextTurn: boolean
}

type StampedView = MessageRunView & {readonly at: number}

export interface RunStore {
  readonly conversationId: string
  readonly status: RunStatus
  /** Client time `status` last changed. */
  readonly statusAt: number
  /** Stop was clicked, in this mode, and the server has not answered yet. */
  readonly stopRequested: StopMode | null
  /** What a stopping run is still waiting for, as its last run.stopping said. */
  readonly waitingFor: RunWaitingFor | null
  readonly messages: Readonly<Record<string, StampedView>>
}

export const IDLE: RunStatus = {
  active: false,
  stopping: false,
  stopMode: null,
  finishingWrite: false,
}
const WORKING: RunStatus = {active: true, stopping: false, stopMode: null, finishingWrite: false}

/** A hard stop overrides a soft one; nothing takes a hard stop back. */
export const strongerStop = (a: StopMode | null, b: StopMode | null): StopMode | null =>
  a === 'hard' || b === 'hard' ? 'hard' : (a ?? b)

// What an event about a message the chat has not seen yet starts from: only a
// waiting message can be steered or withdrawn.
const UNSEEN: MessageRunView = {
  runState: 'queued',
  steered: false,
  hasAttachments: false,
  nextTurn: false,
}

export const emptyRunStore = (conversationId: string): RunStore => ({
  conversationId,
  status: IDLE,
  statusAt: 0,
  stopRequested: null,
  waitingFor: null,
  messages: {},
})

export type RunAction =
  | {type: 'reset'; conversationId: string}
  | {
      type: 'conversationLoaded'
      conversationId: string
      runActive: boolean
      stopping: boolean
      stopMode: StopMode | null
      requestedAt: number
    }
  | {
      type: 'messagesLoaded'
      conversationId: string
      messages: readonly Message[]
      requestedAt: number
    }
  | {type: 'event'; conversationId: string; event: WSEvent; at: number}
  | {type: 'stopRequested'; conversationId: string; mode: StopMode}
  | {
      type: 'stopAnswered'
      conversationId: string
      state: 'stopped' | 'stopping'
      mode: StopMode
      requestedAt: number
    }
  | {type: 'stopFailed'; conversationId: string}

export const viewOfMessage = (message: Message): MessageRunView => ({
  // Payloads from before AB#6760 carry no state: their turn is over.
  runState: message.run_state ?? 'answered',
  steered: Boolean(message.steered),
  hasAttachments: Boolean(message.attachments?.length),
  nextTurn: false,
})

const sameStatus = (a: RunStatus, b: RunStatus): boolean =>
  a.active === b.active &&
  a.stopping === b.stopping &&
  a.stopMode === b.stopMode &&
  a.finishingWrite === b.finishingWrite

const sameView = (a: MessageRunView, b: MessageRunView): boolean =>
  a.runState === b.runState &&
  a.steered === b.steered &&
  a.hasAttachments === b.hasAttachments &&
  a.nextTurn === b.nextTurn

// Every helper hands back the same store when nothing changed, so a streamed
// token does not re-render the chat.
const withStatus = (store: RunStore, status: RunStatus, at: number): RunStore =>
  sameStatus(store.status, status) ? store : {...store, status, statusAt: at}

const changeMessages = (
  store: RunStore,
  ids: readonly string[],
  at: number,
  change: (view: MessageRunView) => MessageRunView,
): RunStore => {
  let messages: Record<string, StampedView> | null = null
  for (const id of ids) {
    const known = store.messages[id]
    const next = change(known ?? UNSEEN)
    if (known && sameView(known, next)) continue
    messages ??= {...store.messages}
    messages[id] = {...next, at}
  }
  return messages ? {...store, messages} : store
}

const changeAllMessages = (
  store: RunStore,
  at: number,
  change: (view: MessageRunView) => MessageRunView,
): RunStore => changeMessages(store, Object.keys(store.messages), at, change)

/** The run ended: what it took in is answered, and what still waits opens the next turn. */
const endRun = (store: RunStore, at: number): RunStore => {
  const settled = changeAllMessages(store, at, (view) => {
    if (view.runState === 'in_run') return {...view, runState: 'answered'}
    if (view.runState === 'queued') return {...view, nextTurn: true}
    return view
  })
  const ended = withStatus(settled, IDLE, at)
  return ended.stopRequested || ended.waitingFor
    ? {...ended, stopRequested: null, waitingFor: null}
    : ended
}

const onEvent = (store: RunStore, event: WSEvent, at: number): RunStore => {
  switch (event.kind) {
    case 'run.started':
      // A run takes everything that is waiting when it starts.
      return withStatus(
        changeAllMessages(store, at, (view) =>
          view.runState === 'queued' ? {...view, runState: 'in_run'} : view,
        ),
        WORKING,
        at,
      )

    // Anything the agent streams means a run works, even if run.started was missed.
    case 'agent.token':
    case 'tool.update':
    case 'attachment.indexing':
      return store.status.active ? store : withStatus(store, {...store.status, active: true}, at)

    case 'run.stopping':
      // Sent again, as hard, when a soft stop is escalated. Without a mode it
      // comes from a backend that only stops hard.
      return withStatus(
        {...store, waitingFor: event.payload.waiting_for ?? null},
        {
          active: true,
          stopping: true,
          stopMode: strongerStop(store.status.stopMode, event.payload.mode ?? 'hard'),
          finishingWrite: Boolean(event.payload.waiting_for),
        },
        at,
      )

    case 'run.stopped':
      return endRun(store, at)

    case 'message.steered':
      return changeMessages(store, event.payload.message_ids, at, (view) => ({
        ...view,
        runState: 'in_run',
        steered: true,
      }))

    case 'message.withdrawn':
      return changeMessages(store, event.payload.message_ids, at, (view) => ({
        ...view,
        runState: 'cancelled',
      }))

    case 'msg.final': {
      const message = event.payload
      if (message.role === 'user') {
        const known = store.messages[message.id]
        const hasAttachments = Boolean(message.attachments?.length)
        // An event about the message can overtake its own msg.final; that state is newer.
        if (known) {
          return known.hasAttachments === hasAttachments
            ? store
            : {...store, messages: {...store.messages, [message.id]: {...known, hasAttachments}}}
        }
        return {
          ...store,
          messages: {...store.messages, [message.id]: {...viewOfMessage(message), at}},
        }
      }
      // The answer, a stopped answer or an error: the run is over.
      if (
        message.role === 'error' ||
        (message.role === 'agent' && message.content?.type === 'text')
      ) {
        return endRun(store, at)
      }
      return store
    }

    default:
      return store
  }
}

export const runReducer = (store: RunStore, action: RunAction): RunStore => {
  if (action.type === 'reset') {
    return store.conversationId === action.conversationId
      ? store
      : emptyRunStore(action.conversationId)
  }
  // Late answers for a conversation the chat has left.
  if (action.conversationId !== store.conversationId) return store

  switch (action.type) {
    case 'conversationLoaded': {
      if (store.statusAt > action.requestedAt) return store
      const status: RunStatus = action.stopping
        ? {
            active: true,
            stopping: true,
            // A stop of unknown mode is offered the escalation: one needless,
            // idempotent hard request beats stranding a soft stop.
            stopMode: action.stopMode ?? 'soft',
            finishingWrite: store.status.finishingWrite,
          }
        : action.runActive
          ? WORKING
          : IDLE
      return sameStatus(store.status, status)
        ? store
        : {...store, status, statusAt: action.requestedAt}
    }

    case 'messagesLoaded': {
      let messages: Record<string, StampedView> | null = null
      for (const message of action.messages) {
        if (message.role !== 'user') continue
        const known = store.messages[message.id]
        if (known && known.at > action.requestedAt) continue
        const next = {...viewOfMessage(message), nextTurn: known?.nextTurn ?? false}
        if (known && sameView(known, next)) continue
        messages ??= {...store.messages}
        messages[message.id] = {...next, at: action.requestedAt}
      }
      return messages ? {...store, messages} : store
    }

    case 'event':
      return onEvent(store, action.event, action.at)

    case 'stopRequested': {
      const mode = strongerStop(store.stopRequested, action.mode)
      return store.stopRequested === mode ? store : {...store, stopRequested: mode}
    }

    case 'stopAnswered': {
      // A soft answer must not clear the hard request that escalated it.
      const answered = store.stopRequested === action.mode ? {...store, stopRequested: null} : store
      // The answer is a read of the server at stop time; the socket may have moved on.
      if (store.statusAt > action.requestedAt) return answered
      const status: RunStatus =
        action.state === 'stopped'
          ? IDLE
          : {
              active: true,
              stopping: true,
              stopMode: strongerStop(store.status.stopMode, action.mode),
              finishingWrite: store.status.finishingWrite,
            }
      return sameStatus(answered.status, status)
        ? answered
        : {...answered, status, statusAt: action.requestedAt}
    }

    case 'stopFailed':
      return store.stopRequested ? {...store, stopRequested: null} : store

    default:
      return store
  }
}

/** Stop has something to stop: a run works or is stopping, or a message waits for one. */
export const isBusy = (store: RunStore): boolean =>
  store.status.active ||
  store.status.stopping ||
  store.stopRequested !== null ||
  Object.values(store.messages).some((view) => view.runState === 'queued')

export type DeliveryLabel = 'queued' | 'next_turn_pending' | 'steered' | 'next_turn' | 'withdrawn'

/**
 * What a user message's bubble says about how it reached Alpinist, or null for
 * an ordinary turn. `runWorking`: a run holds the conversation right now.
 */
export const deliveryLabel = (view: MessageRunView, runWorking: boolean): DeliveryLabel | null => {
  if (view.runState === 'cancelled') return 'withdrawn'
  if (view.steered) return 'steered'
  if (view.runState === 'queued' && runWorking) {
    // A message with attachments is never taken into a run that already works.
    return view.hasAttachments ? 'next_turn_pending' : 'queued'
  }
  if (view.nextTurn) return 'next_turn'
  return null
}
